import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { useChartPreferences } from '../context/ChartPreferencesContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Download, CheckCircle, XCircle, TrendingUp, DollarSign, Clock, Percent, 
  ArrowRight, Filter, Info, Sparkles, ShieldCheck, AlertTriangle, Zap
} from 'lucide-react';
import { formatCurrency, formatPercent, cn } from '../lib/utils';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend
} from 'recharts';

// Helper to cap extreme percentages
const formatCappedPercent = (value, cap = 10000) => {
  if (value > cap) {
    return `+${cap.toLocaleString()}%`;
  }
  if (value < -cap) {
    return `-${cap.toLocaleString()}%`;
  }
  return `${value.toFixed(1)}%`;
};

export const ValuationPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentProject, fetchProject, getCalculation, getDashboard } = useProject();
  const { showLabels, formatChartValue, formatTableValue } = useChartPreferences();
  
  const [valuation, setValuation] = useState(null);
  const [cashFlows, setCashFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartFilter, setChartFilter] = useState('all');

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (!currentProject || currentProject.id !== projectId) {
          await fetchProject(projectId);
        }
        const [valuationData, dashboardData] = await Promise.all([
          getCalculation(projectId, 'valuation'),
          getDashboard(projectId)
        ]);
        setValuation(valuationData);
        setCashFlows(dashboardData?.cash_flow?.cash_flows || []);
      } catch (err) {
        console.error('Error loading valuation:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [projectId, currentProject, fetchProject, getCalculation, getDashboard]);

  const currency = currentProject?.specifications?.currency || 'MXN';
  const wacc = valuation?.wacc || 0;
  const costOfEquity = currentProject?.financing?.cost_of_equity || wacc;
  const projectIRR = valuation?.project_irr || valuation?.irr || 0;
  const equityIRR = valuation?.equity_irr || 0;
  const npv = valuation?.npv || 0;
  
  // Check if all operating cash flows are positive (years 1+)
  const operatingFlowsAllPositive = cashFlows.filter(f => f.year > 0).every(f => (f.operating_cash_flow || 0) >= 0);
  
  // Calculate paybacks
  const projectPayback = valuation?.payback_period || null;
  const equityPayback = valuation?.equity_payback || null;
  
  // Calculate ROIs
  const totalInvestment = currentProject?.financing?.total_investment || 0;
  const equityAmount = currentProject?.financing?.equity_amount || 0;
  const lastYearCF = cashFlows[cashFlows.length - 1] || {};
  const projectROI = totalInvestment > 0 ? ((lastYearCF.cumulative_project_cf || 0) / totalInvestment * 100) : 0;
  const equityROI = equityAmount > 0 ? ((lastYearCF.cumulative_equity_cf || 0) / equityAmount * 100) : 0;

  // Decision criteria with visual indicators
  const criteria = [
    {
      name: 'TIR Proyecto > WACC',
      passed: projectIRR > wacc,
      value: `${formatCappedPercent(projectIRR)} > ${formatCappedPercent(wacc)}`,
      explanation: 'El proyecto genera rendimientos superiores al costo de capital ponderado.',
      icon: TrendingUp,
      colorPassed: 'emerald',
      colorFailed: 'red',
      counts: true
    },
    {
      name: 'TIR Accionista > Costo de Capital',
      passed: equityIRR > costOfEquity,
      value: `${formatCappedPercent(equityIRR)} > ${formatCappedPercent(costOfEquity)}`,
      explanation: 'El retorno para el accionista supera su costo de oportunidad.',
      icon: Zap,
      colorPassed: 'cyan',
      colorFailed: 'red',
      counts: true
    },
    {
      name: 'Flujo Operativo Positivo',
      passed: operatingFlowsAllPositive,
      value: operatingFlowsAllPositive ? 'Todos los años' : 'Hay años negativos',
      explanation: 'La operación genera efectivo cada año sin necesidad de financiamiento adicional.',
      icon: Sparkles,
      colorPassed: 'violet',
      colorFailed: 'orange',
      counts: true
    },
    {
      name: 'VAN Positivo',
      passed: npv > 0,
      value: formatCurrency(npv, currency),
      explanation: 'El valor presente de los flujos futuros supera la inversión inicial.',
      icon: DollarSign,
      colorPassed: 'green',
      colorFailed: 'red',
      counts: true
    }
  ];

  const passingCriteria = criteria.filter(c => c.counts && c.passed).length;
  const totalCriteria = criteria.filter(c => c.counts).length;
  const isAccepted = passingCriteria === totalCriteria;
  const approvalPercentage = (passingCriteria / totalCriteria) * 100;

  // Chart data with 3 lines
  const chartData = cashFlows.map(cf => ({
    year: `Año ${cf.year}`,
    'Flujo Operativo': cf.cumulative_cash_flow || 0,
    'FCF Proyecto': cf.cumulative_project_cf || 0,
    'FCF Accionista': cf.cumulative_equity_cf || 0
  }));

  // Filter chart data based on selection
  const getVisibleLines = () => {
    switch (chartFilter) {
      case 'operational': return ['Flujo Operativo'];
      case 'project': return ['FCF Proyecto'];
      case 'equity': return ['FCF Accionista'];
      default: return ['Flujo Operativo', 'FCF Proyecto', 'FCF Accionista'];
    }
  };

  const lineColors = {
    'Flujo Operativo': '#3B82F6',
    'FCF Proyecto': '#06B6D4',
    'FCF Accionista': '#10B981'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Calculando valuación...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="valuation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs font-medium">PASO 10 DE 12</span>
            <span className="px-2 py-1 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 text-emerald-400 rounded text-xs font-medium">ANÁLISIS COMPLETO</span>
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">Valuación del Proyecto</h1>
          <p className="text-muted-foreground text-sm">Análisis final de factibilidad económica</p>
        </div>
        <Button className="gap-2 bg-gradient-to-r from-primary to-cyan-600 hover:from-primary/90 hover:to-cyan-600/90">
          <Download className="w-4 h-4" />
          Descargar Reporte
        </Button>
      </div>

      {/* Veredicto Principal - Card Grande y Colorido */}
      <Card className={cn(
        "relative overflow-hidden border-2",
        isAccepted 
          ? "border-emerald-500/50 bg-gradient-to-br from-emerald-950/50 via-background to-cyan-950/30" 
          : "border-red-500/50 bg-gradient-to-br from-red-950/50 via-background to-orange-950/30"
      )}>
        {/* Background decoration */}
        <div className={cn(
          "absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-20",
          isAccepted ? "bg-emerald-500" : "bg-red-500"
        )} />
        
        <CardContent className="relative p-8">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div className={cn(
                  "w-16 h-16 rounded-2xl flex items-center justify-center",
                  isAccepted ? "bg-emerald-500/20" : "bg-red-500/20"
                )}>
                  {isAccepted ? (
                    <ShieldCheck className="w-8 h-8 text-emerald-400" />
                  ) : (
                    <AlertTriangle className="w-8 h-8 text-red-400" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wider">Veredicto Final</p>
                  <h2 className={cn("text-4xl font-bold", isAccepted ? "text-emerald-400" : "text-red-400")}>
                    PROYECTO {isAccepted ? 'VIABLE' : 'NO VIABLE'}
                  </h2>
                </div>
              </div>
              
              {/* Progress Bar de Criterios */}
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Criterios aprobados</span>
                  <span className={cn("font-bold", isAccepted ? "text-emerald-400" : "text-orange-400")}>
                    {passingCriteria} de {totalCriteria} ({approvalPercentage.toFixed(0)}%)
                  </span>
                </div>
                <div className="h-3 bg-secondary/50 rounded-full overflow-hidden">
                  <div 
                    className={cn(
                      "h-full rounded-full transition-all duration-1000",
                      isAccepted 
                        ? "bg-gradient-to-r from-emerald-500 to-cyan-500" 
                        : "bg-gradient-to-r from-orange-500 to-red-500"
                    )}
                    style={{ width: `${approvalPercentage}%` }}
                  />
                </div>
              </div>

              <p className="text-muted-foreground max-w-xl text-sm">
                {isAccepted ? (
                  <>
                    <span className="text-emerald-400 font-medium">¡Excelente!</span> Todos los criterios de inversión se cumplen. 
                    El proyecto genera valor y supera los rendimientos mínimos requeridos.
                  </>
                ) : (
                  <>
                    <span className="text-orange-400 font-medium">Atención:</span> El proyecto no cumple con todos los criterios. 
                    Se recomienda revisar los parámetros para mejorar la rentabilidad.
                  </>
                )}
              </p>
            </div>

            {/* Score Circle */}
            <div className="relative">
              <svg className="w-40 h-40 transform -rotate-90">
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="transparent"
                  className="text-secondary"
                />
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  stroke="url(#gradient)"
                  strokeWidth="8"
                  fill="transparent"
                  strokeDasharray={`${approvalPercentage * 4.4} 440`}
                  strokeLinecap="round"
                  className="transition-all duration-1000"
                />
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={isAccepted ? "#10B981" : "#F97316"} />
                    <stop offset="100%" stopColor={isAccepted ? "#06B6D4" : "#EF4444"} />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={cn("text-4xl font-bold", isAccepted ? "text-emerald-400" : "text-orange-400")}>
                  {passingCriteria}/{totalCriteria}
                </span>
                <span className="text-xs text-muted-foreground">Aprobados</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs en Grid Colorido */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-950/50 to-background border-green-500/30 hover:border-green-500/50 transition-all">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-green-400/80 font-medium mb-1">VAN (Valor Actual Neto)</p>
                <p className={cn("text-2xl font-bold", npv > 0 ? "text-green-400" : "text-red-400")}>
                  {formatCurrency(npv, currency)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {npv > 0 ? "Genera valor" : "Destruye valor"}
                </p>
              </div>
              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", npv > 0 ? "bg-green-500/20" : "bg-red-500/20")}>
                <DollarSign className={cn("w-5 h-5", npv > 0 ? "text-green-400" : "text-red-400")} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-950/50 to-background border-blue-500/30 hover:border-blue-500/50 transition-all">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-blue-400/80 font-medium mb-1">TIR del Proyecto</p>
                <p className={cn("text-2xl font-bold", projectIRR > wacc ? "text-blue-400" : "text-red-400")}>
                  {formatCappedPercent(projectIRR)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  vs WACC {formatCappedPercent(wacc)}
                </p>
              </div>
              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", projectIRR > wacc ? "bg-blue-500/20" : "bg-red-500/20")}>
                <Percent className={cn("w-5 h-5", projectIRR > wacc ? "text-blue-400" : "text-red-400")} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-950/50 to-background border-cyan-500/30 hover:border-cyan-500/50 transition-all">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-cyan-400/80 font-medium mb-1">TIR del Accionista</p>
                <p className={cn("text-2xl font-bold", equityIRR > costOfEquity ? "text-cyan-400" : "text-red-400")}>
                  {formatCappedPercent(equityIRR)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  vs Ke {formatCappedPercent(costOfEquity)}
                </p>
              </div>
              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", equityIRR > costOfEquity ? "bg-cyan-500/20" : "bg-red-500/20")}>
                <TrendingUp className={cn("w-5 h-5", equityIRR > costOfEquity ? "text-cyan-400" : "text-red-400")} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-950/50 to-background border-amber-500/30 hover:border-amber-500/50 transition-all">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-amber-400/80 font-medium mb-1">Período de Recuperación</p>
                <p className="text-2xl font-bold text-amber-400">
                  {projectPayback ? `${projectPayback.toFixed(1)} años` : 'N/A'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Tiempo para recuperar inversión
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Criterios de Decisión - Tarjetas Individuales */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Info className="w-5 h-5 text-primary" />
          Criterios de Decisión
          <span className="text-xs text-muted-foreground font-normal ml-2">
            Cada criterio debe cumplirse para aprobar el proyecto
          </span>
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          {criteria.map((criterion, idx) => {
            const IconComponent = criterion.icon;
            const bgColor = criterion.passed 
              ? `bg-${criterion.colorPassed}-950/30` 
              : `bg-${criterion.colorFailed}-950/30`;
            const borderColor = criterion.passed 
              ? `border-${criterion.colorPassed}-500/40` 
              : `border-${criterion.colorFailed}-500/40`;
            
            return (
              <Card 
                key={idx} 
                className={cn(
                  "border transition-all hover:scale-[1.02]",
                  criterion.passed 
                    ? "bg-gradient-to-br from-emerald-950/30 to-background border-emerald-500/30" 
                    : "bg-gradient-to-br from-red-950/30 to-background border-red-500/30"
                )}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0",
                      criterion.passed ? "bg-emerald-500/20" : "bg-red-500/20"
                    )}>
                      <IconComponent className={cn(
                        "w-6 h-6",
                        criterion.passed ? "text-emerald-400" : "text-red-400"
                      )} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-sm">{criterion.name}</h4>
                        <span className={cn(
                          "px-2 py-0.5 rounded-full text-xs font-bold",
                          criterion.passed 
                            ? "bg-emerald-500/20 text-emerald-400" 
                            : "bg-red-500/20 text-red-400"
                        )}>
                          {criterion.passed ? '✓ APROBADO' : '✗ NO APROBADO'}
                        </span>
                      </div>
                      
                      <p className={cn(
                        "text-lg font-mono font-bold mb-1",
                        criterion.passed ? "text-emerald-400" : "text-red-400"
                      )}>
                        {criterion.value}
                      </p>
                      
                      <p className="text-xs text-muted-foreground">
                        {criterion.explanation}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Información Adicional */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-gradient-to-br from-violet-950/30 to-background border-violet-500/20">
          <CardContent className="p-5">
            <h4 className="font-semibold text-violet-400 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Período de Recuperación
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-secondary/30 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Proyecto</p>
                <p className="text-xl font-bold">{projectPayback ? `${projectPayback.toFixed(1)} años` : 'N/A'}</p>
              </div>
              <div className="p-3 bg-secondary/30 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Accionista</p>
                <p className="text-xl font-bold">{equityPayback ? `${equityPayback.toFixed(1)} años` : projectPayback ? `${projectPayback.toFixed(1)} años` : 'N/A'}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Tiempo necesario para recuperar la inversión inicial. A menor tiempo, menor riesgo.
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-pink-950/30 to-background border-pink-500/20">
          <CardContent className="p-5">
            <h4 className="font-semibold text-pink-400 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              ROI (Retorno sobre Inversión)
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-secondary/30 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Proyecto</p>
                <p className="text-xl font-bold">{projectROI > 10000 ? '+10,000%' : `${projectROI.toFixed(0)}%`}</p>
              </div>
              <div className="p-3 bg-secondary/30 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Accionista</p>
                <p className="text-xl font-bold">{equityROI > 10000 ? '+10,000%' : `${equityROI.toFixed(0)}%`}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Rendimiento total acumulado sobre la inversión inicial al final del proyecto.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Multi-Line Chart */}
      <Card className="border-primary/20">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Flujos Acumulados Comparativos</CardTitle>
              <p className="text-sm text-muted-foreground">Evolución de los tres tipos de flujo a lo largo del proyecto</p>
            </div>
            <Select value={chartFilter} onValueChange={setChartFilter}>
              <SelectTrigger className="w-44 h-9 text-xs bg-secondary/50">
                <Filter className="w-3 h-3 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los Flujos</SelectItem>
                <SelectItem value="operational">Flujo Operativo</SelectItem>
                <SelectItem value="project">FCF Proyecto</SelectItem>
                <SelectItem value="equity">FCF Accionista</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
              <XAxis dataKey="year" stroke="#94A3B8" fontSize={11} />
              <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => formatChartValue(v)} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142', fontSize: '12px' }}
                formatter={(value) => formatCurrency(value, currency)}
              />
              <Legend wrapperStyle={{ paddingTop: '10px' }} />
              <ReferenceLine y={0} stroke="#64748B" strokeDasharray="3 3" strokeWidth={2} />
              
              {getVisibleLines().includes('Flujo Operativo') && (
                <Line 
                  type="monotone" 
                  dataKey="Flujo Operativo" 
                  stroke={lineColors['Flujo Operativo']} 
                  strokeWidth={3}
                  dot={{ r: 5, fill: lineColors['Flujo Operativo'] }}
                  activeDot={{ r: 7 }}
                />
              )}
              {getVisibleLines().includes('FCF Proyecto') && (
                <Line 
                  type="monotone" 
                  dataKey="FCF Proyecto" 
                  stroke={lineColors['FCF Proyecto']} 
                  strokeWidth={3}
                  dot={{ r: 5, fill: lineColors['FCF Proyecto'] }}
                  activeDot={{ r: 7 }}
                />
              )}
              {getVisibleLines().includes('FCF Accionista') && (
                <Line 
                  type="monotone" 
                  dataKey="FCF Accionista" 
                  stroke={lineColors['FCF Accionista']} 
                  strokeWidth={3}
                  dot={{ r: 5, fill: lineColors['FCF Accionista'] }}
                  activeDot={{ r: 7 }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-secondary/20">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3 text-sm text-primary">Resumen de Inversión</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Inversión Total</span>
                <span className="font-medium">{formatCurrency(totalInvestment, currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Capital Propio</span>
                <span className="font-medium">{formatCurrency(equityAmount, currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Deuda</span>
                <span className="font-medium">{formatCurrency(currentProject?.financing?.debt_amount || 0, currency)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-secondary/20">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3 text-sm text-cyan-400">Tasas de Descuento</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">WACC</span>
                <span className="font-medium">{formatPercent(wacc)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Costo de Deuda (Kd)</span>
                <span className="font-medium">{formatPercent(currentProject?.financing?.cost_of_debt || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Costo de Capital (Ke)</span>
                <span className="font-medium">{formatPercent(costOfEquity)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-secondary/20">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3 text-sm text-emerald-400">Métricas de Rentabilidad</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Índice de Rentabilidad</span>
                <span className="font-medium">{valuation?.profitability_index?.toFixed(2) || 'N/A'}x</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">ROI Proyecto</span>
                <span className="font-medium">{projectROI > 10000 ? '+10,000%' : `${projectROI.toFixed(0)}%`}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">ROI Accionista</span>
                <span className="font-medium">{equityROI > 10000 ? '+10,000%' : `${equityROI.toFixed(0)}%`}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Button */}
      <div className="flex justify-end">
        <Button 
          size="lg" 
          onClick={() => navigate(`/app/project/${projectId}/scenarios`)}
          className="gap-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500"
        >
          Analizar Escenarios y Sensibilidad
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
